import { Fragment, jsx } from "react/jsx-runtime";
import { Adapt, useAdaptParent } from "@tamagui/adapt";
import { useComposedRefs } from "@tamagui/compose-refs";
import {
  getVariableValue,
  isWeb,
  styled,
  useGet,
  useIsomorphicLayoutEffect,
  withStaticProperties
} from "@tamagui/core";
import { getSpace } from "@tamagui/get-token";
import { ListItem } from "@tamagui/list-item";
import { PortalHost } from "@tamagui/portal";
import { Separator } from "@tamagui/separator";
import { Sheet, SheetController } from "@tamagui/sheet";
import { ThemeableStack, XStack, YStack } from "@tamagui/stacks";
import { Paragraph, SizableText } from "@tamagui/text";
import { useControllableState } from "@tamagui/use-controllable-state";
import * as React from "react";
import { SELECT_NAME } from "./constants";
import { SelectProvider, createSelectContext, useSelectContext } from "./context";
import { SelectContent } from "./SelectContent";
import { SelectInlineImpl } from "./SelectImpl";
import { SelectScrollDownButton, SelectScrollUpButton } from "./SelectScrollButton";
import { SelectViewport } from "./SelectViewport";
import {
  useSelectBreakpointActive,
  useShowSelectSheet
} from "./useSelectBreakpointActive";
const TRIGGER_NAME = "SelectTrigger";
const SelectTrigger = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeSelect, disabled = false, ...triggerProps } = props;
    const context = useSelectContext(TRIGGER_NAME, __scopeSelect);
    if (context.shouldRenderWebNative) {
      return null;
    }
    return /* @__PURE__ */ jsx(
      ListItem,
      {
        componentName: TRIGGER_NAME,
        backgrounded: true,
        radiused: true,
        hoverTheme: true,
        pressTheme: true,
        focusTheme: true,
        focusable: true,
        borderWidth: 1,
        size: context.size,
        "aria-expanded": context.open,
        "aria-autocomplete": "none",
        dir: context.dir,
        disabled,
        "data-disabled": disabled ? "" : void 0,
        ...triggerProps,
        ref: forwardedRef,
        ...process.env.TAMAGUI_TARGET === "web" && context.interactions ? context.interactions.getReferenceProps() : {
          onPress() {
            context.setOpen(!context.open);
          }
        }
      }
    );
  }
);
SelectTrigger.displayName = TRIGGER_NAME;
const VALUE_NAME = "SelectValue";
const SelectValueFrame = styled(Paragraph, {
  name: VALUE_NAME,
  userSelect: "none"
});
const SelectValue = SelectValueFrame.extractable(
  React.forwardRef(
    ({
      __scopeSelect,
      children: childrenProp,
      placeholder
    }, forwardedRef) => {
      const context = useSelectContext(VALUE_NAME, __scopeSelect);
      const composedRefs = useComposedRefs(forwardedRef, context.onValueNodeChange);
      const children = childrenProp ?? context.selectedItem;
      const isEmptyValue = context.value == null || context.value === "";
      const selectValueChildren = isEmptyValue ? placeholder ?? children : children;
      return /* @__PURE__ */ jsx(
        SelectValueFrame,
        {
          size: context.size,
          ref: composedRefs,
          pointerEvents: "none",
          children: selectValueChildren
        }
      );
    }
  )
);
SelectValue.displayName = VALUE_NAME;
const SelectIcon = styled(XStack, {
  name: "SelectIcon",
  // @ts-ignore
  "aria-hidden": true,
  children: /* @__PURE__ */ jsx(Paragraph, { children: "\u25BC" })
});
const ITEM_NAME = "SelectItem";
const [SelectItemContextProvider, useSelectItemContext] = createSelectContext(ITEM_NAME);
const SelectItem = React.forwardRef(
  (props, forwardedRef) => {
    const {
      __scopeSelect,
      value,
      disabled = false,
      textValue: textValueProp,
      index,
      ...itemProps
    } = props;
    const context = useSelectContext(ITEM_NAME, __scopeSelect);
    const isSelected = context.value === value;
    const textId = React.useId();
    const {
      selectedIndex,
      setSelectedIndex,
      listRef,
      open,
      setOpen,
      onChange,
      setActiveIndex,
      allowMouseUpRef,
      allowSelectRef,
      setValueAtIndex,
      selectTimeoutRef,
      dataRef
    } = context;
    const composedRefs = useComposedRefs(forwardedRef, (node) => {
      if (!isWeb)
        return;
      if (node instanceof HTMLElement) {
        if (listRef) {
          listRef.current[index] = node;
        }
      }
    });
    useIsomorphicLayoutEffect(() => {
      setValueAtIndex(index, value);
    }, [index, setValueAtIndex, value]);
    function handleSelect() {
      setSelectedIndex(index);
      onChange(value);
      setOpen(false);
    }
    const selectItemProps = context.interactions ? context.interactions.getItemProps({
      onTouchStart() {
        allowSelectRef.current = true;
        allowMouseUpRef.current = false;
      },
      onKeyDown(event) {
        if (event.key === "Enter" || event.key === " " && !(dataRef == null ? void 0 : dataRef.current.typing)) {
          event.preventDefault();
          handleSelect();
        } else {
          allowSelectRef.current = true;
        }
      },
      onClick() {
        if (allowSelectRef.current) {
          setSelectedIndex(index);
          setOpen(false);
        }
      },
      onMouseUp() {
        if (!allowMouseUpRef.current) {
          return;
        }
        if (allowSelectRef.current) {
          handleSelect();
        }
        clearTimeout(selectTimeoutRef.current);
        selectTimeoutRef.current = setTimeout(() => {
          allowSelectRef.current = true;
        });
      }
    }) : {
      onPress: handleSelect
    };
    return /* @__PURE__ */ jsx(
      SelectItemContextProvider,
      {
        scope: __scopeSelect,
        value,
        textId: textId || "",
        isSelected,
        children: context.shouldRenderWebNative ? /* @__PURE__ */ jsx("option", { value, children: props.children }) : /* @__PURE__ */ jsx(
          ListItem,
          {
            tag: "div",
            backgrounded: true,
            pressTheme: true,
            hoverTheme: true,
            cursor: "default",
            outlineWidth: 0,
            componentName: ITEM_NAME,
            ref: composedRefs,
            "aria-labelledby": textId,
            "aria-selected": isSelected,
            "data-state": isSelected ? "active" : "inactive",
            "aria-disabled": disabled || void 0,
            "data-disabled": disabled ? "" : void 0,
            tabIndex: disabled ? void 0 : -1,
            size: context.size,
            focusStyle: {
              backgroundColor: "$backgroundHover"
            },
            ...itemProps,
            ...selectItemProps
          }
        )
      }
    );
  }
);
SelectItem.displayName = ITEM_NAME;
const ITEM_TEXT_NAME = "SelectItemText";
const SelectItemTextFrame = styled(SizableText, {
  name: ITEM_TEXT_NAME,
  variants: {
    unstyled: {
      false: {
        userSelect: "none",
        color: "$color"
      }
    }
  },
  defaultVariants: {
    unstyled: false
  }
});
const SelectItemText = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeSelect, className, ...itemTextProps } = props;
    const context = useSelectContext(ITEM_TEXT_NAME, __scopeSelect);
    const ref = React.useRef(null);
    const composedRefs = useComposedRefs(forwardedRef, ref);
    const itemContext = useSelectItemContext(ITEM_TEXT_NAME, __scopeSelect);
    const isSelected = Boolean(itemContext.isSelected && context.valueNode);
    const contents = React.useMemo(
      () => /* @__PURE__ */ jsx(
        SelectItemTextFrame,
        {
          className,
          size: context.size,
          id: itemContext.textId,
          ...itemTextProps,
          ref: composedRefs
        }
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [props, context.size, className, itemContext.textId]
    );
    useIsomorphicLayoutEffect(() => {
      if (isSelected) {
        context.setSelectedItem(contents);
      }
    }, [isSelected, contents]);
    if (context.shouldRenderWebNative)
      return /* @__PURE__ */ jsx(Fragment, { children: props.children });
    return /* @__PURE__ */ jsx(Fragment, { children: contents });
  }
);
SelectItemText.displayName = ITEM_TEXT_NAME;
const ITEM_INDICATOR_NAME = "SelectItemIndicator";
const SelectItemIndicatorFrame = styled(XStack, {
  name: ITEM_TEXT_NAME
});
const SelectItemIndicator = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeSelect, ...itemIndicatorProps } = props;
    const context = useSelectContext(ITEM_INDICATOR_NAME, __scopeSelect);
    const itemContext = useSelectItemContext(ITEM_INDICATOR_NAME, __scopeSelect);
    if (context.shouldRenderWebNative) {
      return null;
    }
    return itemContext.isSelected ? /* @__PURE__ */ jsx(SelectItemIndicatorFrame, { "aria-hidden": true, ...itemIndicatorProps, ref: forwardedRef }) : null;
  }
);
SelectItemIndicator.displayName = ITEM_INDICATOR_NAME;
const GROUP_NAME = "SelectGroup";
const [SelectGroupContextProvider, useSelectGroupContext] = createSelectContext(GROUP_NAME);
const SelectGroupFrame = styled(YStack, {
  name: GROUP_NAME,
  width: "100%"
});
const NativeSelectTextFrame = styled(SizableText, {
  tag: "select",
  backgroundColor: "$background",
  borderColor: "$borderColor",
  hoverStyle: {
    backgroundColor: "$backgroundHover"
  }
});
const NativeSelectFrame = styled(ThemeableStack, {
  name: "NativeSelect",
  bordered: true,
  userSelect: "none",
  outlineWidth: 0,
  paddingRight: 10,
  variants: {
    size: {
      "...size": (val, extras) => {
        const { tokens } = extras;
        const paddingHorizontal = getVariableValue(tokens.space[val]);
        return {
          borderRadius: tokens.radius[val] ?? val,
          minHeight: tokens.size[val],
          paddingRight: paddingHorizontal + 20,
          paddingLeft: paddingHorizontal,
          paddingVertical: getSpace(val, {
            shift: -4
          })
        };
      }
    }
  },
  defaultVariants: {
    size: "$2"
  }
});
const SelectGroup = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeSelect, ...groupProps } = props;
    const groupId = React.useId();
    const context = useSelectContext(GROUP_NAME, __scopeSelect);
    const size = context.size ?? "$true";
    const nativeSelectRef = React.useRef(null);
    const content = function() {
      if (context.shouldRenderWebNative) {
        return (
          // @ts-expect-error until we support typing based on tag
          /* @__PURE__ */ jsx(NativeSelectFrame, { asChild: true, size, value: context.value, children: /* @__PURE__ */ jsx(
            NativeSelectTextFrame,
            {
              onChange: (event) => {
                context.onChange(event.currentTarget.value);
              },
              size,
              ref: nativeSelectRef,
              style: {
                color: "var(--color)",
                // @ts-ignore
                appearance: "none"
              },
              children: props.children
            }
          ) })
        );
      }
      return /* @__PURE__ */ jsx(
        SelectGroupFrame,
        {
          role: "group",
          "aria-labelledby": groupId,
          ...groupProps,
          ref: forwardedRef
        }
      );
    }();
    return /* @__PURE__ */ jsx(SelectGroupContextProvider, { scope: __scopeSelect, id: groupId || "", children: content });
  }
);
SelectGroup.displayName = GROUP_NAME;
const LABEL_NAME = "SelectLabel";
const SelectLabel = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeSelect, ...labelProps } = props;
    const context = useSelectContext(LABEL_NAME, __scopeSelect);
    const groupContext = useSelectGroupContext(LABEL_NAME, __scopeSelect);
    if (context.shouldRenderWebNative) {
      return null;
    }
    return /* @__PURE__ */ jsx(
      ListItem,
      {
        tag: "div",
        componentName: LABEL_NAME,
        fontWeight: "800",
        id: groupContext.id,
        size: context.size,
        ...labelProps,
        ref: forwardedRef
      }
    );
  }
);
SelectLabel.displayName = LABEL_NAME;
const SelectSeparator = styled(Separator, {
  name: "SelectSeparator"
});
const SelectSheetController = (props) => {
  const context = useSelectContext("SelectSheetController", props.__scopeSelect);
  const showSheet = useShowSelectSheet(context);
  const breakpointActive = useSelectBreakpointActive(context.sheetBreakpoint);
  const getShowSheet = useGet(showSheet);
  return /* @__PURE__ */ jsx(
    SheetController,
    {
      onOpenChange: (val) => {
        if (getShowSheet()) {
          props.onOpenChange(val);
        }
      },
      open: context.open,
      hidden: breakpointActive === false,
      children: props.children
    }
  );
};
const SelectSheetImpl = (props) => {
  return /* @__PURE__ */ jsx(Fragment, { children: props.children });
};
const Select = withStaticProperties(
  (props) => {
    const {
      __scopeSelect,
      native,
      children,
      open: openProp,
      defaultOpen,
      onOpenChange,
      value: valueProp,
      defaultValue,
      onValueChange,
      size: sizeProp = "$true",
      dir
    } = props;
    const id = React.useId();
    const scopeKey = __scopeSelect ? Object.keys(__scopeSelect)[0] ?? id : id;
    const { when, AdaptProvider } = useAdaptParent({
      Contents: React.useCallback(
        () => /* @__PURE__ */ jsx(PortalHost, { name: `${scopeKey}SheetContents` }),
        [scopeKey]
      )
    });
    const sheetBreakpoint = when;
    const isSheet = useSelectBreakpointActive(sheetBreakpoint);
    const SelectImpl = isSheet || !isWeb ? SelectSheetImpl : SelectInlineImpl;
    const forceUpdate = React.useReducer(() => ({}), {})[1];
    const [selectedItem, setSelectedItem] = React.useState(null);
    const [open, setOpen] = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen || false,
      onChange: onOpenChange
    });
    const [value, setValue] = useControllableState({
      prop: valueProp,
      defaultProp: defaultValue || "",
      onChange: onValueChange,
      transition: true
    });
    const [activeIndex, setActiveIndex] = React.useState(0);
    const selectedIndexRef = React.useRef(null);
    const activeIndexRef = React.useRef(null);
    const listContentRef = React.useRef([]);
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [valueNode, setValueNode] = React.useState(null);
    useIsomorphicLayoutEffect(() => {
      selectedIndexRef.current = selectedIndex;
      activeIndexRef.current = activeIndex;
    });
    const shouldRenderWebNative = isWeb && (native === true || native === "web" || Array.isArray(native) && native.includes("web"));
    return /* @__PURE__ */ jsx(AdaptProvider, { children: /* @__PURE__ */ jsx(
      SelectProvider,
      {
        dir,
        blockSelection: false,
        size: sizeProp,
        fallback: false,
        selectedItem,
        setSelectedItem,
        forceUpdate,
        valueNode,
        onValueNodeChange: setValueNode,
        scopeKey,
        sheetBreakpoint,
        scope: __scopeSelect,
        setValueAtIndex: (index, value2) => {
          listContentRef.current[index] = value2;
        },
        activeIndex,
        onChange: setValue,
        selectedIndex,
        setActiveIndex,
        setOpen,
        setSelectedIndex,
        value,
        open,
        native,
        shouldRenderWebNative,
        children: /* @__PURE__ */ jsx(SelectSheetController, { onOpenChange: setOpen, __scopeSelect, children: shouldRenderWebNative ? children : /* @__PURE__ */ jsx(
          SelectImpl,
          {
            activeIndexRef,
            listContentRef,
            selectedIndexRef,
            ...props,
            open,
            value,
            children
          }
        ) })
      }
    ) });
  },
  {
    Adapt,
    Content: SelectContent,
    Group: SelectGroup,
    Icon: SelectIcon,
    Item: SelectItem,
    ItemIndicator: SelectItemIndicator,
    ItemText: SelectItemText,
    Label: SelectLabel,
    ScrollDownButton: SelectScrollDownButton,
    ScrollUpButton: SelectScrollUpButton,
    Trigger: SelectTrigger,
    Value: SelectValue,
    Viewport: SelectViewport,
    Sheet: Sheet.Controlled
  }
);
Select.displayName = SELECT_NAME;
export {
  Select,
  SelectGroupFrame,
  SelectIcon,
  SelectItem,
  SelectItemTextFrame,
  SelectSeparator,
  SelectTrigger
};
//# sourceMappingURL=Select.js.map
